import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Folder as FolderIcon, Plus } from "lucide-react";
import type { Folder } from "@/types/linguadeck";

export interface SaveToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  deckId: string;
  folders: Folder[];
  onCreateFolder: (title: string) => Promise<Folder>;
  onSave: (folderId: string) => Promise<void>;
}

export default function SaveToFolderModal({
  isOpen,
  onClose,
  deckId,
  folders,
  onCreateFolder,
  onSave,
}: SaveToFolderModalProps) {
  const { t } = useTranslation();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderTitle, setNewFolderTitle] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedFolderId(null);
      setIsSubmitting(false);
      setShowNewFolderInput(false);
      setNewFolderTitle("");
      setIsCreatingFolder(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderTitle.trim();
    if (!trimmed || isCreatingFolder) return;
    setIsCreatingFolder(true);
    try {
      const folder = await onCreateFolder(trimmed);
      setShowNewFolderInput(false);
      setNewFolderTitle("");
      setSelectedFolderId(folder.id);
    } catch (err) {
      console.error("[SaveToFolderModal] create folder error:", err);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolderId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSave(selectedFolderId);
      onClose();
    } catch (err) {
      console.error("[SaveToFolderModal] save error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm bg-black/60 transition-opacity"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-xl bg-white dark:bg-[hsl(var(--bg2))] border border-[hsl(var(--border))] p-6 transition-all duration-200 flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="text-xl font-semibold mb-4 font-[Instrument_Serif] shrink-0"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {t("saveToFolder.title")}
        </h3>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          {/* New folder button / inline create */}
          <div className="mb-3 shrink-0">
            {!showNewFolderInput ? (
              <button
                type="button"
                onClick={() => setShowNewFolderInput(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--foreground))] transition-colors text-sm font-medium"
              >
                <Plus size={18} strokeWidth={2} />
                {t("saveToFolder.newFolder")}
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderTitle}
                  onChange={(e) => setNewFolderTitle(e.target.value)}
                  placeholder={t("folder.namePlaceholder")}
                  autoFocus
                  className="flex-1 px-3 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateFolder();
                    }
                    if (e.key === "Escape") {
                      setShowNewFolderInput(false);
                      setNewFolderTitle("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleCreateFolder}
                  disabled={!newFolderTitle.trim() || isCreatingFolder}
                  className="px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingFolder ? "…" : t("folder.createFolder")}
                </button>
              </div>
            )}
          </div>

          {/* Folder list — scrollable only; header, New folder, and actions stay fixed */}
          <div className="max-h-[40vh] min-h-0 overflow-y-auto space-y-1 mb-5 pr-1 py-0.5">
            {folders.length === 0 && !showNewFolderInput ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] py-2">
                {t("folder.noFolders")}
              </p>
            ) : (
              folders.map((folder) => (
                <label
                  key={folder.id}
                  className={`flex items-center gap-3 py-2.5 px-3 rounded-xl border cursor-pointer transition-colors ${
                    selectedFolderId === folder.id
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 dark:bg-[hsl(var(--primary))]/15"
                      : "border-[hsl(var(--border))] hover:bg-[hsl(var(--bg3))]"
                  }`}
                >
                  <input
                    type="radio"
                    name="saveToFolder"
                    value={folder.id}
                    checked={selectedFolderId === folder.id}
                    onChange={() => setSelectedFolderId(folder.id)}
                    className="sr-only"
                  />
                  <FolderIcon
                    size={20}
                    className="shrink-0 text-[hsl(var(--muted-foreground))]"
                  />
                  <span
                    className="text-sm font-medium truncate"
                    style={{ color: "hsl(var(--foreground))" }}
                  >
                    {folder.title}
                  </span>
                </label>
              ))
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium text-sm hover:border-[hsl(var(--primary))] transition-colors"
            >
              {t("folder.cancel")}
            </button>
            <button
              type="submit"
              disabled={!selectedFolderId || isSubmitting}
              className="flex-1 py-3 px-4 rounded-xl border-0 text-white font-semibold text-sm bg-[hsl(var(--primary))] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isSubmitting ? "…" : t("saveToFolder.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
